import { useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { selectUser } from 'gossamer-front-end';
import { useEventPaymentData } from './useEventPaymentData';
import { useBookingExists } from './useBookingExists';

type BookingStatus = 'confirmed' | 'timeout' | 'payment_succeeded' | 'error' | 'cancelled';

const HEADING_TEXT: Record<BookingStatus, string> = {
  confirmed: 'Payment complete!',
  payment_succeeded: 'Payment processing…',
  error: 'Payment failed',
  cancelled: 'Payment cancelled',
  timeout: 'Booking delayed',
};

const HEADING_STYLE: Record<BookingStatus, string> = {
  confirmed: 'text-green-600',
  payment_succeeded: 'text-yellow-600',
  error: 'text-red-600',
  cancelled: 'text-gray-600',
  timeout: 'text-yellow-600',
};

const DETAIL_TEXT: Record<BookingStatus, string> = {
  confirmed: 'Your payment has been made and your booking is confirmed.',
  payment_succeeded: 'Your payment has been made and your booking is processing...',
  error: 'There was an issue confirming your booking. Please contact support.',
  cancelled: 'Your payment and booking have been cancelled.',
  timeout:
    'Your payment has been made, but your booking is still being confirmed — please check your email or your Booked Events page shortly.',
};

export interface EventPaymentReturnModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: number;
  redirectStatus: 'succeeded' | 'failed' | 'canceled' | 'processing';
}

// Return Modal: Used for the post-payment return/callback
export const EventPaymentReturnModal = (props: EventPaymentReturnModalProps) => {
  const user = useSelector(selectUser);
  const { eventRecord } = useEventPaymentData(props.eventId);
  const bookingExistence = useBookingExists(props.eventId, user?.userId, true);

  const event = eventRecord.data;
  // Use redirectStatus or paymentIntent status for display

  const bookingStatus: BookingStatus =
    props.redirectStatus === 'failed'
      ? 'error'
      : props.redirectStatus === 'canceled'
        ? 'cancelled'
        : props.redirectStatus !== 'succeeded'
          ? 'error'
          : bookingExistence === 'exists'
            ? 'confirmed'
            : bookingExistence === 'timed_out'
              ? 'timeout'
              : 'payment_succeeded';

  return (
    <Modal show={props.open} onHide={() => props.onOpenChange(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Payment status</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {event ? (
          <>
            <h2 className="text-lg font-semibold mt-2 mb-2">{event.title}</h2>
            <div className={`my-4 ${HEADING_STYLE[bookingStatus]} font-semibold`}>{HEADING_TEXT[bookingStatus]}</div>
            <div className="mt-2 text-sm text-muted-foreground">{DETAIL_TEXT[bookingStatus]}</div>
          </>
        ) : (
          <div>Loading event details…</div>
        )}
      </Modal.Body>
    </Modal>
  );
};
